import React, { useEffect, useRef, useState } from 'react';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import styled from 'styled-components';
import belowIcon from '../../../asset/icon/below.png';
import { searchArtistMember } from '../../../shared/api/memberApi';
import type { MemberSearchData } from '../../../shared/dto/EntityRepository';
import { useModalStore } from '../../../shared/store/portal/modalStore';

const WorkArtistSearchModalMobile: React.FC = () => {
  const { standardModal, clearModal } = useModalStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MemberSearchData[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const latestSearchId = useRef(0);

  useEffect(() => {
    const normalizedQuery = query.trim();
    const searchId = ++latestSearchId.current;

    if (!normalizedQuery) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      return;
    }

    setIsSearching(true);
    setSearchFailed(false);
    const timer = window.setTimeout(async () => {
      try {
        const response = await searchArtistMember(normalizedQuery);
        if (latestSearchId.current !== searchId) return;
        setResults(response.data.members ?? []);
      } catch {
        if (latestSearchId.current !== searchId) return;
        setResults([]);
        setSearchFailed(true);
      } finally {
        if (latestSearchId.current === searchId) setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(timer);
      if (latestSearchId.current === searchId) latestSearchId.current += 1;
    };
  }, [query]);

  const handleClose = () => {
    clearModal(500);
  };

  const handleSelect = (member: MemberSearchData) => {
    standardModal?.handleChange(String(member.aui), member);
    handleClose();
  };

  return (
    <Container>
      <TitleWrapper>
        <Title>Connect artist account</Title>
        <UniversalIconBtn
          size="fit"
          border={false}
          isTransparent
          backgroundImg={belowIcon}
          onClick={handleClose}
          aria-label="Close"
        />
      </TitleWrapper>
      <SearchInput
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search Architrave artists"
        aria-label="Search Architrave artists"
        autoFocus
      />
      <SearchResults>
        {isSearching && <SearchMessage>Searching…</SearchMessage>}
        {!isSearching && searchFailed && <SearchMessage>Search failed.</SearchMessage>}
        {!isSearching && !searchFailed && results.length === 0 && (
          <SearchMessage>No results.</SearchMessage>
        )}
        {!isSearching && results.map((member) => (
          <SearchResult key={member.aui} type="button" onClick={() => handleSelect(member)}>
            <ResultName>{member.username}</ResultName>
            <ResultHandle>@{member.handle}</ResultHandle>
          </SearchResult>
        ))}
      </SearchResults>
    </Container>
  );
};

export default WorkArtistSearchModalMobile;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const TitleWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: clamp(12px, 2vh, 30px);
  margin-bottom: clamp(20px, 8vh, 30px);
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.color_Gray_02};
  ${({ theme }) => theme.typography.Body_01_1};
`;


const SearchInput = styled.input`
  width: 100%;
  padding: 6px 0;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  outline: none;
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SearchResults = styled.div`
  min-height: 38vh;
  max-height: 50vh;
  overflow-y: auto;
`;

const SearchResult = styled.button`
  width: 100%;
  padding: 12px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  background: transparent;
  border: none;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_06};
  text-align: left;
  cursor: pointer;
  ${({ theme }) => theme.typography.Body_04};
`;

const ResultName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ResultHandle = styled.span`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const SearchMessage = styled.div`
  min-height: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  color: ${({ theme }) => theme.colors.color_Gray_05};
  text-align: center;
  ${({ theme }) => theme.typography.Body_04};
`;
